import {AfterViewInit, Component, ElementRef, OnInit, Renderer2, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {MatPaginator} from "@angular/material/paginator";
import {MatSort} from "@angular/material/sort";
import {UtilsService} from "../shared/utils.service";
import {AppService} from "../app.service";
import {ApiRequestService} from "../shared/api-request.service";
import {MatDialog} from "@angular/material/dialog";
import {tap} from "rxjs/operators";
import {saveAs} from "file-saver";
import moment from "moment";
import {UserPublicProfileComponent} from "../shared/user-public-profile/user-public-profile.component";
import {ApiDataSource} from "../utils/api-data-source";
import {AssetModel} from "../models/asset.model";
import {AssetsFilterComponent} from "./assets-filter/assets-filter.component";
import {AssetsViewComponent} from "./assets-view/assets-view.component";
import {AssetsEditComponent} from "./assets-edit/assets-edit.component";
import {AssetsService} from "./assets.service";
import {AssetsFilters} from "./assets-filter/assets-filter.interface";
import {RBACService} from "../shared/rbac.service";
import {Router} from "@angular/router";

@Component({
  selector: 'app-assets',
  templateUrl: './assets.component.html',
  styleUrls: ['./assets.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AssetsComponent implements OnInit, AfterViewInit {

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  dataSource = new AssetsDataSource(this.app, this.api);

  // RBAC permission flags: resolved once in ngOnInit().
  canCreate: boolean = false;
  canView: boolean = false;
  canEdit: boolean = false;
  canDelete: boolean = false;

  // The tag filter is a cross-feature surface: it lists the account's tags, which belong to the
  // Account: Tags feature. It was gated on the assets view permission, so a user who could see assets
  // but not tags still rendered it, and its unconditional GET v2/tags returned a 403 that surfaced as
  // an error dialog on page load. Matches the flag used by the Sites and Incidents lists.
  canViewTags: boolean = false;

  // Build displayedColumns dynamically (NOT as a static array).
  displayedColumns: string[] = [];

  constructor(
    public utils: UtilsService,
    public app: AppService,
    private api: ApiRequestService,
    private dialog: MatDialog,
    public renderer: Renderer2,
    public assetsService: AssetsService,
    public rbac: RBACService,
    private router: Router
  ) { }

  ngOnInit() {
    // 1. Resolve RBAC permission flags.
    this.canCreate = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_ASSETS, RBACService.RBAC_A.A_CREATE)
    ], true);
    this.canView = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_ASSETS, RBACService.RBAC_A.A_VIEW)
    ], true);
    this.canEdit = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_ASSETS, RBACService.RBAC_A.A_EDIT)
    ], true);
    this.canDelete = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_ASSETS, RBACService.RBAC_A.A_DELETE)
    ], true);
    this.canViewTags = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_ACCOUNT_TAGS, RBACService.RBAC_A.A_VIEW)
    ], true);

    // 2. Page-level access: redirect if user has NO permissions at all.
    if (!this.canCreate && !this.canView && !this.canEdit && !this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to access assets.');
      this.router.navigate(['/dashboard']);
      return;
    }

    // 3. Build displayedColumns dynamically.
    this.displayedColumns = [];
    if (this.canView || this.canDelete) {
      this.displayedColumns.push('select');
    }
    this.displayedColumns.push(
      'id',
      'name',
      'code',
      'asset_type',
      'flags',
      'status',
      'owner_type',
      'maintenance_due_date',
      'next_inspection_due_date',
      'condition_rating',
      'site',
      'buttons'
    );

    // 4. Only load data if user has view permission.
    if (this.canView) {
      this.dataSource.getData(false);
    }
  }

  ngAfterViewInit() {
    // The table, its matSort and its mat-paginator all live inside a container gated by
    // *ngIf="canView", so without that permission Angular never creates those view
    // children and this.sort / this.paginator stay undefined. Bail out before wiring them up.
    if (!this.canView) {
      return;
    }

    // Reset the paginator when sorting
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    const _tap = tap(() => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.order = this.sort.direction;

      // sorting for utc time by parsing original time
      if (this.sort.active === "date_created_UTC") {
        this.dataSource.order_by = "date_created";
      } else {
        this.dataSource.order_by = this.sort.active;
      }

      this.dataSource.getData(false);
    });

    // Subscribe to the paginator tap events
    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  /**
   * Create a new Asset.
   *
   * @return {void}
   */
  onAdd(): void {
    if (!this.canCreate) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to create assets.');
      return;
    }
    // Deliberately not routed through onEdit(), which would check the edit permission and block a
    // create-only user.
    this.openDialog(new AssetModel());
  }

  /**
   * Update an existing Asset.
   *
   * @param {AssetModel} asset The asset to be edited.
   * @return {void}
   */
  onEdit(asset: AssetModel): void {
    if (!this.canEdit) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to edit assets.');
      return;
    }
    this.openDialog(asset);
  }

  /**
   * Opens the create/edit dialog. Shared by onAdd() and onEdit(), which each guard their own
   * permission before calling this.
   *
   * @param {AssetModel} asset The asset to be created or edited.
   * @return {void}
   */
  private openDialog(asset: AssetModel): void {
    this.dialog
      .open(AssetsEditComponent, {
        width: '90%',
        data: {
          asset_id: asset.id
        }
      })
      .afterClosed()
      .subscribe(() => {
        // Refresh the list regardless of how the dialog is closed, but only for a user who may
        // actually see the list.
        if (this.canView) {
          this.dataSource.getData(true);
        }
      });
  }

  /**
   * Display the Asset data.
   *
   * @param {number} id - The unique identifier of the asset.
   * @return {void}
   */
  onView(id: number): void {
    if (!this.canView) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to view assets.');
      return;
    }
    this.utils.showComponentDialog(
      AssetsViewComponent,
      {
        asset_id: id
      },
      { width: '90%' },
      () => {
        // Refresh the list regardless of how the dialog is closed.
        this.dataSource.getData(true);
      }
    );
  }

  /**
   * Archives selected Assets.
   *
   * @return {void}
   */
  onRemoveSelected(): void {
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to archive assets.');
      return;
    }
    this.utils.showModal(
      'Archive Asset(s)',
      `Are you sure you want to archive ${this.dataSource.selection.selected.length} asset(s)?`,
      () => {
        this.remove(this.dataSource.selection.selected.join(','), 'Asset(s) have been archived.');
      }
    );
  }

  /**
   * Archives the specified Asset.
   *
   * @param {AssetModel} asset - The asset to be archived.
   * @return {void}
   */
  onRemove(asset: AssetModel): void {
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to archive assets.');
      return;
    }
    this.utils.showModal(
      'Archive Asset',
      `Are you sure you want to archive "${asset.name}"?`,
      () => {
        this.remove(asset.id, 'Asset has been archived.');
      }
    );
  }

  /**
   * Archives Assets with the specified IDs.
   *
   * @param {any} ids - IDs of assets to be removed.
   * @param msg
   * @return {void}
   */
  private remove(ids: any, msg: string): void {
    this.api.makeRequest('delete', `v2/assets/${ids}`)
      .then((response) => {
        this.utils.showToast(msg);
        this.dataSource.selection.clear();
        this.dataSource.getData(false);
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Restore Asset.
   *
   * @param {number} id - The ID of the asset to be restored.
   * @return {void}
   */
  onRestore(id: number): void {
    // Restoring is the inverse of archiving, so it is governed by the delete permission.
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to restore assets.');
      return;
    }
    this.utils.showModal(
      'Restore Asset',
      'Are you sure you want to restore this asset?',
      () => {
        this.restore(id, 'The Asset was restored.');
      }
    );
  }

  /**
   * Restore selected Assets.
   *
   * @return {void}
   */
  onRestoreSelected(): void {
    // Restoring is the inverse of archiving, so it is governed by the delete permission.
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to restore assets.');
      return;
    }
    this.utils.showModal(
      'Restore Selected Asset(s)',
      'Are you sure you want to restore the selected assets?',
      () => {
        this.restore(this.dataSource.selection.selected.join(','), 'The selected Asset(s) were restored.');
      }
    );
  }

  /**
   * Restores Assets with the specified IDs.
   *
   * @param {any} ids - IDs of assets to be restored.
   * @param msg
   * @return {void}
   */
  private restore(ids: any, msg: string): void {
    this.api.makeRequest('put', `v2/assets/${ids}/restore`)
      .then((response) => {
        this.utils.showToast(msg);
        this.dataSource.selection.clear();
        this.dataSource.getData();
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Sets the tags for the filters and updates the asset filter preferences.
   * Retrieves the data from the API using the updated filters.
   *
   * @param {number[]} event
   * @return {void}
   */
  getOutputTagsFilter(event: number[]): void {
    this.dataSource.tag_ids = event;
    this.dataSource.getData(true);
  }

  /**
   * Show component dialog for filtering Assets.
   *
   * @return {void}
   */
  onFilter(): void {
    // Filtering only narrows the list, so it is meaningless without the view permission.
    if (!this.canView) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to view assets.');
      return;
    }
    this.utils.showComponentDialog(AssetsFilterComponent, {
        archived: this.dataSource.filters.archived,
        asset_type_ids: this.dataSource.filters.asset_type_ids,
        site_ids: this.dataSource.filters.site_ids,
        contractor_owner_ids: this.dataSource.filters.contractor_owner_ids,
        vendor_ids: this.dataSource.filters.vendor_ids,
        contractor_ids: this.dataSource.filters.contractor_ids,
        user_ids: this.dataSource.filters.user_ids,
        status: this.dataSource.filters.status,
        owner_type: this.dataSource.filters.owner_type,
        condition_rating: this.dataSource.filters.condition_rating,
        is_visible: this.dataSource.filters.is_visible,
        is_mobile: this.dataSource.filters.is_mobile,
        is_safety_critical: this.dataSource.filters.is_safety_critical,
        maintenance_record_due_status: this.dataSource.filters.maintenance_record_due_status,
        date_type: this.dataSource.filters.date_type,
        date_range: this.dataSource.filters.date_range
      }, {
        width: '1200px'
      },
      (results: any): void => {
        // Terminate early if it is undefined or an empty string.
        if ( typeof results === 'undefined' || results === '' ) {
          return;
        }

        // Apply the filters.
        this.dataSource.filters.archived = results.archived ?? this.dataSource.filters.archived;
        this.dataSource.filters.asset_type_ids = results.asset_type_ids ?? this.dataSource.filters.asset_type_ids;
        this.dataSource.filters.site_ids = results.site_ids ?? this.dataSource.filters.site_ids;
        this.dataSource.filters.contractor_owner_ids = results.contractor_owner_ids ?? this.dataSource.filters.contractor_owner_ids;
        this.dataSource.filters.vendor_ids = results.vendor_ids ?? this.dataSource.filters.vendor_ids;
        this.dataSource.filters.contractor_ids = results.contractor_ids ?? this.dataSource.filters.contractor_ids;
        this.dataSource.filters.user_ids = results.user_ids ?? this.dataSource.filters.user_ids;
        this.dataSource.filters.status = results.status ?? this.dataSource.filters.status;
        this.dataSource.filters.owner_type = results.owner_type ?? this.dataSource.filters.owner_type;
        this.dataSource.filters.condition_rating = results.condition_rating ?? this.dataSource.filters.condition_rating;
        this.dataSource.filters.is_visible = results.is_visible ?? this.dataSource.filters.is_visible;
        this.dataSource.filters.is_mobile = results.is_mobile ?? this.dataSource.filters.is_mobile;
        this.dataSource.filters.is_safety_critical = results.is_safety_critical ?? this.dataSource.filters.is_safety_critical;
        this.dataSource.filters.maintenance_record_due_status = results.maintenance_record_due_status ?? this.dataSource.filters.maintenance_record_due_status;
        this.dataSource.filters.date_type = results.date_type ?? this.dataSource.filters.date_type;
        this.dataSource.filters.date_range = results.date_range ?? this.dataSource.filters.date_range;
        this.dataSource.getData();
      }
    );
  }

  /**
   * Exports the list of Assets into the specified format and sends a download request to the browser.
   *
   * @param type csv, xls, xlsx, pdf.
   */
  onExportSelected(type: string = 'csv'):void {
    // Exporting reveals the records themselves, so it requires the view permission.
    if (!this.canView) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to export assets.');
      return;
    }
    this.dataSource.makeDownloadRequest(`v2/assets/export/${type}` + (this.dataSource.selection.selected.length ? '/' + this.dataSource.selection.selected.join(',') : ''), {
      archived: this.dataSource.filters.archived,
      asset_type_ids: this.dataSource.filters.asset_type_ids.join(','),
      site_ids: this.dataSource.filters.site_ids.join(','),
      contractor_owner_ids: this.dataSource.filters.contractor_owner_ids.join(','),
      vendor_ids: this.dataSource.filters.vendor_ids.join(','),
      contractor_ids: this.dataSource.filters.contractor_ids.join(','),
      user_ids: this.dataSource.filters.user_ids.join(',') ,
      status: this.dataSource.filters.status ?? 'all',
      owner_type: this.dataSource.filters.owner_type ?? 'all',
      condition_rating: this.dataSource.filters.condition_rating ?? 'all',
      is_visible: this.dataSource.filters.is_visible ?? 'all',
      is_mobile: this.dataSource.filters.is_mobile ?? 'all',
      is_safety_critical: this.dataSource.filters.is_safety_critical ?? 'all',
      maintenance_record_due_status: this.dataSource.filters.maintenance_record_due_status ?? 'all',
      date_type: this.dataSource.filters.date_type,
      date_range: this.dataSource.filters.date_range.map((date: Date) => {
        return date.getTime() / 1000;
      }).join(','),
      tag_ids: this.dataSource.tag_ids.join(',')
    })
      .then((response) => {
        if(type == 'xlsx') {
          this.utils.showToast(response.message || 'Your export is queued in the background. You will receive an email when it is ready.');
        } else {
          saveAs(response, `Assets - ${moment().toString()}.${type}`);
        }
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Open the User's public profile dialog.
   *
   * @param hash
   */
  onUserPublicView(hash: string):void {
    this.utils.showComponentDialog(
      UserPublicProfileComponent,
      hash,
      { width: '90%' },
      () => {
        // Refresh the list regardless of how the dialog is closed.
        // this.dataSource.getData();
      }
    );
  }

  protected readonly RBACService = RBACService;
  protected readonly AppService = AppService;
}

export class AssetsDataSource extends ApiDataSource {
  order_by = 'id';
  order = 'asc';

  tag_ids: number[] = [];

  filters: AssetsFilters = {
    archived: 'false',
    asset_type_ids: [],
    site_ids: [],
    contractor_owner_ids: [],
    vendor_ids: [],
    contractor_ids: [],
    user_ids: [],
    status: 'all',
    owner_type: 'all',
    condition_rating: 'all',
    is_visible: 'all',
    is_mobile: 'all',
    is_safety_critical: 'all',
    maintenance_record_due_status: 'all',
    date_type: 'maintenance_due_date',
    date_range: []
  };

  getData(resetOffset: boolean = false) {
    this.makeRequest('v2/assets', resetOffset, {
      archived: this.filters.archived,
      asset_type_ids: this.filters.asset_type_ids.join(','),
      site_ids: this.filters.site_ids.join(','),
      contractor_owner_ids: this.filters.contractor_owner_ids.join(','),
      vendor_ids: this.filters.vendor_ids.join(','),
      contractor_ids: this.filters.contractor_ids.join(','),
      user_ids: this.filters.user_ids.join(',') ,
      status: this.filters.status ?? 'all',
      owner_type: this.filters.owner_type ?? 'all',
      condition_rating: this.filters.condition_rating ?? 'all',
      is_visible: this.filters.is_visible ?? 'all',
      is_mobile: this.filters.is_mobile ?? 'all',
      is_safety_critical: this.filters.is_safety_critical ?? 'all',
      maintenance_record_due_status: this.filters.maintenance_record_due_status ?? 'all',
      date_type: this.filters.date_type,
      date_range: this.filters.date_range.map((date: Date) => {
        return date.getTime() / 1000;
      }).join(','),
      tag_ids: this.tag_ids.join(',')
    });
  }
}
